import { has, differenceBy, trim } from 'lodash';
import moment from 'moment';
import { getTranslationByLocale } from '../../utils/TranslationUtils';

/**
 * Prepares venues for the API call
 * @param {Array} venues
 * @param {string} locale language locale
 * @returns {Object}
 */
export const formatVenues = (venues, locale) => venues.map((venue) => {
  const result = {
    catalogId: venue.name,
    aliases: createVenueAliases(venue),
    placeId: venue.bookingId,
    restaurantId: venue.name,
    name: getTranslationByLocale(venue.title, locale),
    groups: {
      Country: venue.visitationAddress ? venue.visitationAddress.country : '',
      Area: venue.area,
    },
  };

  if (venue.classifications) {
    result.groups.Cluster = [venue.classifications];
  }

  if (venue.openingDate) {
    result.groups['Same Store Sales'] = moment(venue.openingDate).isBefore(moment().subtract(14, 'month')).toString();
  } else if (!has(venue, 'openingDate')) {
    result.groups['Same Store Sales'] = '-';
  }

  if (venue.franchiseGroup && venue.franchiseGroup.length > 0) {
    result.groups['Franchise Group'] = [venue.franchiseGroup];
    // venue.franchiseGroup.forEach((group) => result.groups.franchiseGroup.push(group));
  }
  return result;
});

const createVenueAliases = (venue) => {
  const result = [];
  if (venue.bookingAliases) {
    venue.bookingAliases.forEach((alias) => result.push({ placeId: alias }));
  }

  result.push({ restaurantId: venue.legacyVenueId });
  return result;
};

/**
 * Prepares products for the API call
 * @param {Array} products
 * @param {string} locale
 * @returns {Object}
 */
export const formatProducts = (products, locale) => products.map((product) => {
  const result = {
    catalogId: product.code,
    name: trim(getTranslationByLocale(product.title, locale)),
    // aliases: product.title.map((translation) => translation ? translation.content ? { name: translation.content } : undefined : undefined).filter((item) => !!item),
    aliases: product.alias.map((a) => ({ name: trim(a) })),
    similar: product.similar.map((s) => ({ name: trim(s) })),
    groups: {
      Alcoholic: product.alcoholic ? product.alcoholic.toString() : 'false',
      'Main Category': product.mainCategory,
      Category: product.category ? getTranslationByLocale(product.category.title, locale) : '',
      'Default Product': product.default ? product.default.toString() : 'false',
    },
  };

  if (product.costPerPortion !== undefined) {
    let cost = product.costPerPortion.replace(/,/g, '.');
    cost = parseFloat(cost);
    result.cost = cost;
  }

  if (product.articleGroups.length > 0) {
    product.articleGroups.forEach((article) => { result.groups[article] = 'true'; });
  }

  return result;
});

// /**
//  * Gets the default products of a market
//  * @param {Array} products
//  * @returns {Array}
//  */
// export const getDefaultProducts = (products) => products.map((product) => {
//   if (product.markets.filter((market) => has(market, 'locked')).length > 0) {
//     const markets = product.markets.filter((market) => market.locked === true);
//     if (markets.length > 0) {
//       const newProduct = { ...product };
//       markets.forEach((market) => newProduct[`default`] = true); // eslint-disable-line
//       return newProduct;
//     }
//     return undefined;
//   }
//   return undefined;
// }).filter((item) => !!item);

/**
 * Gets the default products of a market
 * @param {Array} products
 * @returns {Array}
 */
export const getDefaultProducts = (products) => products.filter((product) => product.default === true);

/**
 * Sets 'default' property on products according to their markets
 * @param {Array} products array to modify
 * @returns {Array} retursn array with products modified with 'default' property
 */
export const setDefaultsOnProducts = (products) => products.map((product) => {
  if (product.markets.filter((market) => has(market, 'locked')).length > 0) {
    const markets = product.markets.filter((market) => market.locked === true);
    if (markets.length > 0) {
      const newProduct = { ...product };
      markets.forEach((market) => newProduct[`default`] = true); // eslint-disable-line
      return newProduct;
    }
    return { ...product, default: false };
  }
  return { ...product, default: false };
});

/**
 * Formats data for AutoComplete to match an id with a name to display
 * @param {Array} c The data to format - e.g. products or venues
 * @param {string} locale language locale
 * @returns {Array}
 */
export const dataConfigFormatter = (c, locale) => c.map((choice) => ({ ...choice, text: getTranslationByLocale(choice.title, locale) }));

/**
 * Filtering intial items from selected items
 * @param {Array} initialValues Original array
 * @param {Array} selectedValues The array to compare the orignal to
 * @returns {Array}
 */
export const filterChosenItems = (initialValues = [], selectedValues = []) => {
  if (selectedValues.length > 0) {
    return differenceBy(initialValues, selectedValues, '_id');
  }
  return initialValues;
};

/**
 * Filters venues based on the area manager
 * @param {Array} venues Array of all venues to filter
 * @param {string} selectedManager The name of the manager you want to filter by
 * @param {Object} manager User Object of an area manager
 * @returns {Array}
 */
export const filterByAreaManager = (venues, selectedManager, areaManagers) => {
  let currentManager;
  const str = new RegExp(`${selectedManager}`, 'gi');
  areaManagers.forEach((manager) => manager.fullName.match(str) ? currentManager = manager : null); // eslint-disable-line no-return-assign
  const { permissions } = currentManager;
  const managerVenues = Object.keys(permissions);
  return venues.filter((venue) => managerVenues.includes(venue.name));
};

/**
 * Filters products according to a given market
 * @param {Array} products An array of products to be filtered
 * @param {string} market The market code (SE, NO, DK, FI) to filter by
 * @returns {Array}
 */
export const filterProductsByMarket = (products = [], market) => products.filter((product) => product.markets.some(((mrkt) => mrkt.suggestedPrice && mrkt.countryCode === market)));

/**
 * Filters venues according to a given market
 * @param {Array} venues An array of venues to be filtered
 * @param {string} countryCode The market code (SE, NO, DK, FI) to filter by
 * @returns {Array}
 */
export const filterVenuesByMarket = (venues = [], countryCode) => venues.filter((venue) => venue.market.countryCode === countryCode);

/**
 * Filters categories according to a given market
 * @param {Array} categories An array of categories to be filtered
 * @param {string} countryCode The market code (SE, NO, DK, FI) to filter by
 * @returns {Array}
 */
export const filterCategoriesByMarket = (categories = [], countryCode) => categories.filter((cat) => cat.markets.some((market) => market.countryCode === countryCode));


/**
 * Filter venues based on a cluster
 * @param {Array} venues An array of venues
 * @param {string} cluster A string representing a value inside the classifications property on the venue (Storstad, Kuststad, etc)
 * @returns {Array}
 */
export const filterByCluster = (venues, cluster) => venues.filter((venue) => venue.classifications === cluster);

/**
 * Filter venues based on their opening date
 * @param {Array} venues Array of venues
 * @returns {Array} Array of venues with an opening date further back than 14 months from now
 */
export const filterByTime = (venues, date) => venues.filter((venue) => moment(venue.openingDate).isBefore(moment(date).subtract(14, 'month')));

/**
 * Filters products based on a category
 * @param {Array} products Array of products
 * @param {string} category The category to filter by
 */
export const filterByCategory = (products, category) => products.filter((prod) => prod.category ? category.includes(prod.category._id) : false);

/**
 * Sets an 'area' property on a venue based on which area manager currently controls it based on their firebase user object venue array
 * @param {Array} venues Array of venue objects
 * @param {Array} areaManagers Array of firebase user objects
 * @returns {Array} Returns a modified copy of the original array
 */
export const setAreaOnVenue = (venues, areaManagers = []) => {
  const tempVenues = [...venues];
  areaManagers.forEach((manager) => {
    const { permissions } = manager;
    // eslint-disable-next-line prefer-destructuring
    const managerName = manager.fullName.split(' ')[0];
    const managerVenues = Object.keys(permissions);
    tempVenues.forEach((venue) => {
      if (managerVenues.includes(venue.name)) {
        venue.area = managerName; // eslint-disable-line no-param-reassign
      }
    });
  });
  return tempVenues;
};

export const addAliasToProduct = (products, alias) => products.map((product) => {
  const matchingAlias = alias.find((a) => a.product === product.code);
  if (matchingAlias) {
    return {
      ...product,
      alias: matchingAlias.alias,
      similar: matchingAlias.similar,
    };
  }
  return { ...product, alias: [], similar: [] };
});

export const createSimilarProducts = (products) => {
  const results = [];
  products.forEach((product) => { // creme brulee
    if (product.similar.length > 0) {
      product.similar.forEach((similarProduct) => { // Daimbrownie
        results.push({
          markets: product.markets,
          code: similarProduct,
          title: [{ lang: 'en', content: similarProduct }],
          alcoholic: product.alcoholic ? product.alcoholic : false,
          mainCategory: product.mainCategory,
          category: product.category,
          default: false,
          alias: [],
          similar: [],
          articleGroups: product.articleGroups,
        });
      });
    }
    results.push(product);
  });
  return results;
};

export const getDrinksProducts = (products) => products.filter((p) => p.category.mainCategory === 'drink');

export const getFoodProducts = (products) => products.filter((p) => p.category.mainCategory === 'food');

export const filterData = (data, products, allProducts) => {
  if (allProducts) return data;
  const productIds = products.reduce((acc, next) => [...acc, next.code], []);
  const newData = data.filter((d) => d.find((dd) => productIds.includes(dd)));
  newData.unshift(data[0]);
  return newData;
};
